import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useGetHaloDefaultsQuery,
  type IDealData,
  type IOptionDealRow,
  type OptionsDealStreamSource,
} from "@pulse-ui/api-client";
import { formatTalosDateTime } from "@pulse-ui/formatting";
import { useWebSocketRows, type UseWebSocketRowsResult } from "./useWebSocketRows";

const DEALS_ENDPOINT = "/api/trading-ops/v1/deals";
const OPTION_DEALS_WS_ENDPOINT = "/api/trading-ops/v1/option-deals/ws";

export const DEAL_STREAM_TOPIC = "deal_event";

export interface DealStatusMeta {
  label: string;
  terminal: boolean;
  subscribable: boolean;
}

// Closed and Terminated are marked non-subscribable because Talos doesn't actually emit them —
// subscribing to a status Talos never sends would break the whole deal-stream subscription.
export const DEAL_STATUSES = {
  Pending: { label: "Pending", terminal: false, subscribable: true },
  New: { label: "New", terminal: false, subscribable: true },
  Live: { label: "Live", terminal: false, subscribable: true },
  Closed: { label: "Closed", terminal: true, subscribable: false },
  Canceled: { label: "Canceled", terminal: true, subscribable: true },
  Expired: { label: "Expired", terminal: true, subscribable: true },
  Terminated: { label: "Terminated", terminal: true, subscribable: false },
} satisfies Record<string, DealStatusMeta>;

export type DealStatusKey = keyof typeof DEAL_STATUSES;

export const SUBSCRIBABLE_STATUSES = Object.entries(DEAL_STATUSES)
  .filter(([, meta]) => meta.subscribable)
  .map(([status]) => status)
  .join(",");

export function isTerminalDealStatus(status: string | undefined): boolean {
  return status !== undefined && (DEAL_STATUSES[status as DealStatusKey]?.terminal ?? false);
}

export interface DealsStreamRange {
  startDate?: Date | null;
  endDate?: Date | null;
}

// The store feed takes the same query and ignores Statuses; it never emits Pending or New.
export function dealsStreamEndpoint(
  opts: DealsStreamRange,
  source: OptionsDealStreamSource = "talos",
): string {
  const base = source === "store" ? OPTION_DEALS_WS_ENDPOINT : DEALS_ENDPOINT;
  const params = new URLSearchParams();
  if (opts.startDate) params.set("StartDate", formatTalosDateTime(opts.startDate));
  if (opts.endDate) params.set("EndDate", formatTalosDateTime(opts.endDate));
  params.set("Statuses", SUBSCRIBABLE_STATUSES);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

// A Talos deal carries no store provenance; it reads as one with Origin/BookingId/BookingKind unset.
export function toOptionDealRow(deal: IDealData | IOptionDealRow): IOptionDealRow {
  return { Origin: null, BookingId: null, BookingKind: null, ...deal };
}

// A failed or still-loading defaults query reads as the Talos feed, so the stream opens at once
// and only re-keys to the store feed if the defaults say so.
export function resolveDealStreamSource(
  source: OptionsDealStreamSource | undefined,
): OptionsDealStreamSource {
  return source === "store" ? "store" : "talos";
}

// Talos frames carry IDealData; normalize fills the store-only fields once per incoming row.
const getKey = (d: IOptionDealRow) => d.ID;
const getRevision = (d: IOptionDealRow) => d.Revision;
const transform = (map: Map<string, IOptionDealRow>) => Array.from(map.values());

export interface UseDealStreamOptions {
  startDate?: Date | null;
  endDate?: Date | null;
  // ANDed with "a date bound is present" to decide the live subscription; never an unbounded
  // stream. Defaults to true.
  enabled?: boolean;
  paused?: boolean;
}

export interface UseDealStreamResult extends UseWebSocketRowsResult<IOptionDealRow> {
  enabled: boolean;
  source: OptionsDealStreamSource;
  // Earliest trade time the store holds; null on the Talos feed or before the first frame.
  horizon: string | null;
}

// True when the requested range starts before the store's horizon, so older deals are missing.
export function isBeforeDealHorizon(
  startDate: Date | null | undefined,
  horizon: string | null,
): boolean {
  if (!startDate || !horizon) return false;
  const horizonMs = Date.parse(horizon);
  return Number.isFinite(horizonMs) && startDate.getTime() < horizonMs;
}

// The live deal stream, shared by every widget that needs a deal's current status (the Deals
// widget, option-strategy Active/Inactive derivation, and similar). halo-defaults picks the feed.
export function useDealStream({
  startDate,
  endDate,
  enabled = true,
  paused,
}: UseDealStreamOptions): UseDealStreamResult {
  const { data: defaults } = useGetHaloDefaultsQuery();
  const source = resolveDealStreamSource(defaults?.options_deal_stream_source);
  const live = enabled && (!!startDate || !!endDate);
  const endpoint = useMemo(
    () => dealsStreamEndpoint({ startDate, endDate }, source),
    [startDate, endDate, source],
  );
  const [horizon, setHorizon] = useState<string | null>(null);
  useEffect(() => setHorizon(null), [endpoint]);
  const onFrame = useCallback((frame: { data: IOptionDealRow[]; horizon?: string | null }) => {
    if (frame.horizon) setHorizon(frame.horizon);
  }, []);
  const stream = useWebSocketRows<IOptionDealRow>({
    endpoint,
    topic: DEAL_STREAM_TOPIC,
    getKey,
    getRevision,
    normalize: toOptionDealRow,
    transform,
    enabled: live,
    paused,
    onFrame,
  });
  return { ...stream, enabled: live, source, horizon };
}
